import { useState } from "react";
import { PersonRemove } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import Center from "../../../components/Center";
import Error from "../../../components/Error";
import Loading from "../../../components/Loading";
import { QueryRenderer } from "../../../components/QueryRenderer";
import { UserInfo } from "../../../components/ResourceInfo";
import {
  useDeleteGroupAssociation,
  useGroupAssociations,
} from "../../../domain/crud";
import { useGroupParams } from "../../../paths";
import type { GroupAssociation } from "../../../services/datastore";
import { AddUserDialog } from "./AddUserDialog";

export function UserManagement() {
  const [showDialog, setShowDialog] = useState(false);

  const { groupId } = useGroupParams();

  const groupAssociationsQuery = useGroupAssociations({ groupId, limit: 100 });

  function handleOpenDialog() {
    setShowDialog(true);
  }

  function handleCloseDialog() {
    setShowDialog(false);
  }

  return (
    <Card sx={{ mt: 4 }}>
      <CardHeader
        title="User Management"
        titleTypographyProps={{ component: "h2" }}
        action={
          groupAssociationsQuery.isSuccess && (
            <Button variant="contained" onClick={handleOpenDialog}>
              Add User
            </Button>
          )
        }
      />
      <CardContent>
        <QueryRenderer
          query={groupAssociationsQuery}
          loading={<Loading type="circular" />}
          error={
            <Error>
              <Typography variant="h5" component="p">
                Unable to loads group's users
              </Typography>
            </Error>
          }
          success={({ data }) => (
            <>
              {data.length === 0 ? (
                <Center>
                  <Typography
                    variant="h6"
                    component="p"
                    sx={{ fontStyle: "italic", my: 4 }}
                  >
                    No users belong to this group
                  </Typography>
                </Center>
              ) : (
                <List>
                  {data.map((groupAssociation) => (
                    <GroupMembership
                      key={groupAssociation.id}
                      groupAssociation={groupAssociation}
                    />
                  ))}
                </List>
              )}
              <AddUserDialog
                groupId={groupId}
                open={showDialog}
                onClose={handleCloseDialog}
                groupAssociations={data}
              />
            </>
          )}
        />
      </CardContent>
    </Card>
  );
}

function GroupMembership({
  groupAssociation,
}: {
  groupAssociation: GroupAssociation;
}) {
  const { userId } = groupAssociation;

  const deleteGroupAssociation = useDeleteGroupAssociation(groupAssociation.id);

  function handleDelete() {
    deleteGroupAssociation.mutate();
  }

  return (
    <ListItem
      secondaryAction={
        <Tooltip title="Remove user from this group">
          <span>
            <IconButton
              disabled={deleteGroupAssociation.isLoading}
              onClick={handleDelete}
            >
              <PersonRemove />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <UserInfo
        userId={userId}
        chip={<Chip label={groupAssociation.role} size="small" />}
      />
    </ListItem>
  );
}
